import {
  findChildrenInRange,
  mergeAttributes,
  Node,
  nodeInputRule,
  Tracker,
  Editor
} from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import FigureNodeView from '@/components/FigureNodeView.vue'

export interface FigureOptions {
  caption: string
  HTMLAttributes: Record<string, any>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    figure: {
      /**
       * Add a figure element
       */
      setFigure: (options: {
        src: string
        alt?: string
        title?: string
        id?: string
        style?: string
        caption?: string
      }) => ReturnType

      /**
       * Converts an image to a figure
       */
      imageToFigure: () => ReturnType

      /**
       * Converts a figure to an image
       */
      figureToImage: () => ReturnType
    }
  }
}
function defineFigureAttributes() {
  return {
    src: {
      default: null,
      parseHTML: (element: any) => element.querySelector('img')?.getAttribute('src')
    },

    alt: {
      default: null,
      parseHTML: (element: any) => element.querySelector('img')?.getAttribute('alt')
    },

    title: {
      default: null,
      parseHTML: (element: any) => element.querySelector('img')?.getAttribute('title')
    },
    width: {
      default: null,
      parseHTML: (element: any) => element.querySelector('img')?.getAttribute('width')
    },
    height: {
      default: null,
      parseHTML: (element: any) => element.querySelector('img')?.getAttribute('height')
    },
    class: {
      default: null,
      parseHTML: (element: any) => element.querySelector('img')?.getAttribute('class')
    },
    id: {
      default: null,
      parseHTML: (element: any) => element.querySelector('img')?.getAttribute('id')
    },
    longdesc: {
      default: null,
      parseHTML: (element: any) => element.querySelector('img')?.getAttribute('longdesc')
    },
    style: {
      default: null,
      parseHTML: (element: any) => element.getAttribute('style')
    },
    'data-node-id': {
      default: null,
      parseHTML: (element: any) => element.querySelector('img')?.getAttribute('data-node-id')
    },
    caption: {
      default: null,
      parseHTML: (element: any) => {
        return element.querySelector('figcaption')?.innerHTML
      }
    },
    link: {
      default: null,
      parseHTML: (element: any) => {
        if (element.querySelector('a')) {
          let el = element.querySelector('a');

          return el.getAttributeNames()
          .reduce((obj: any, name: string) => ({
             ...obj,
             [name]: el.getAttribute(name)
          }), {})
        } else {
          return null
        }
      }
    }
  } as Record<string, any>
}
function getAttrs(element: any) {
  const attributes = defineFigureAttributes()
  return Object.keys(attributes).reduce(
    (attrs, key) => {
      const parseFn = attributes[key].parseHTML
      attrs[key] = parseFn ? parseFn(element) : null
      return attrs
    },
    {} as Record<string, any>
  )
}
export const inputRegex = /!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\)/

export const MAMfigure = Node.create<FigureOptions>({
  name: 'figure',

  addOptions() {
    return {
      caption: '',
      HTMLAttributes: {}
    }
  },

  group: 'block',

  content: 'inline*',

  draggable: true,

  isolating: true,

  selectable: true, // <-- important

  addAttributes() {
    return defineFigureAttributes()
  },
  parseHTML() {
    return [
      {
        tag: 'figure',
        contentElement: 'figcaption',
        getAttrs: (element) => getAttrs(element) // inputRule taking over ?
      }
    ]
  },

  renderHTML({ HTMLAttributes }) {
    if (HTMLAttributes.link) {
      return [
        'figure',
        (({ style }) => {
          return { style }
        })(HTMLAttributes),
        [
          'a',
          (({ link }) => {
              return { ...link };
          })(HTMLAttributes),
          ['img',
            (({ link, ...attrs }) => { 
              return  mergeAttributes(attrs, { draggable: false, contenteditable: false });
             })(HTMLAttributes)
          ]
        ],
        ['figcaption', { style: 'margin-left: 4px; overflow-wrap: break-word;' }, 0]
      ]
    } else {
      return [
        'figure',
        (({ style }) => {
          return { style }
        })(HTMLAttributes),
        ['img', mergeAttributes(HTMLAttributes, { draggable: false, contenteditable: false })],
        ['figcaption', { style: 'margin-left: 4px; overflow-wrap: break-word;' }, 0]
      ]
    }
  },
  addNodeView() {
    return VueNodeViewRenderer(FigureNodeView)
  },
  onUpdate() {
    const { editor } = this
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'figure') {
        const updatedCaption = node?.content.firstChild?.text
        if (updatedCaption != undefined && updatedCaption !== node.attrs.caption) {
          editor.commands.updateAttributes('figure', { caption: updatedCaption })
        }
      }
    })
  },
  addCommands() {
    return {
      setFigure:
        ({ caption, ...attrs }) =>
          ({ chain }) => {
            return (
              chain()
                .insertContent({
                  type: this.name,
                  attrs,
                  content: caption ? [{ type: 'text', text: caption }] : []
                })
                .updateAttributes('figure', { caption })
                // set cursor at end of caption field
                .command(({ tr, commands }) => {
                  const { doc, selection } = tr
                  const position = doc.resolve(selection.to - 2).end()

                  return commands.setTextSelection(position)
                })
                .run()
            )
          },
      // (options) =>
      //        ({ commands, caption, ...attrs }) => {
      //   return commands.insertContent({
      //     type: this.name,
      //     attrs,
      //     content: caption ? [{ type: 'text', text: caption }] : []
      //   })
      // },
      // ({ }) =>
      // ({ chain }) => {
      //   return (
      //     chain()
      //       .insertContent({

      //       // set cursor at end of caption field
      //       .command(({ tr, commands }) => {
      //         const { doc, selection } = tr
      //         const position = doc.resolve(selection.to - 2).end()

      //         return commands.setTextSelection(position)
      //       })
      //       .run()
      //   )
      // },

      imageToFigure:
        () =>
          ({ tr, commands }) => {
            const { doc, selection } = tr
            const { from, to } = selection
            const images = findChildrenInRange(
              doc,
              { from, to },
              (node) => node.type.name === 'image'
            )

            if (!images.length) {
              return false
            }

            const tracker = new Tracker(tr)

            return commands.forEach(images, ({ node, pos }) => {
              const mapResult = tracker.map(pos)

              if (mapResult.deleted) {
                return false
              }

              const range = {
                from: mapResult.position,
                to: mapResult.position + node.nodeSize
              }

              return commands.insertContentAt(range, {
                type: this.name,
                attrs: {
                  ...node.attrs
                }
              })
            })
          },

      figureToImage:
        () =>
          ({ tr, commands }) => {
            const { doc, selection } = tr
            const { from, to } = selection
            const figures = findChildrenInRange(
              doc,
              { from, to },
              (node) => node.type.name === this.name
            )

            if (!figures.length) {
              return false
            }

            const tracker = new Tracker(tr)

            return commands.forEach(figures, ({ node, pos }) => {
              const mapResult = tracker.map(pos)

              if (mapResult.deleted) {
                return false
              }

              const range = {
                from: mapResult.position,
                to: mapResult.position + node.nodeSize
              }

              return commands.insertContentAt(range, {
                type: 'image',
                attrs: {
                  ...node.attrs
                }
              })
            })
          }
    }
  }
  // no shorthand for figures
  //  addInputRules() {
  //   return [
  //     nodeInputRule({
  //       // find: /<figcaption>(.*?)<\/figcaption>/,
  //       find: inputRegex,
  //       type: this.type,
  //       getAttributes: (match) => {
  //         // Logic to extract attributes from the matched input
  //         console.log('found figure: ', match)
  //         return { caption: match[1] || '' }
  //       }
  //     })
  //   ]
  // },
  // addEventHandlers() {
  //   return {
  //     dblclick: (event, view) => {
  //       // const { schema, doc, selection } = view.state;
  //       // const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
  //       // if (!pos) return false;

  //       // const node = doc.nodeAt(pos.pos);
  //       console.log('in extension handler', event, view)
  //       return this.options.onDoubleClick()
  //     }
  //   }
  // }
})
